import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Mic2, Trophy, Users, Calculator, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COMPANY_INFO, TALENT_ROSTER, SERVICES } from '../data/companyData';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', duration = 2 }) => {
  const [count, setCount] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_177ec162-a789-4cad-ab14-accead9ebd01/artifacts/53213un4_Logo%20Meraki.png";

const HomePage = () => {
  const { language, t } = useLanguage();
  const featuredTalent = TALENT_ROSTER.filter(t => t.featured).slice(0, 4);
  const serviceIcons = { Mic2, Trophy, Users, Calculator };

  return (
    <>
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover">
            <source src="https://assets.mixkit.co/videos/preview/mixkit-people-dancing-at-a-night-club-4344-large.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/90 via-[#050505]/70 to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <motion.img 
            src={LOGO_URL} 
            alt="MERAKI Entertainment" 
            className="h-28 md:h-36 w-auto mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-outfit text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter uppercase mb-6 text-white"
          >
            <span className="block">{language === 'es' ? 'TALENTO GLOBAL.' : 'GLOBAL TALENT.'}</span>
            <span className="block text-[#D91616]">{language === 'es' ? 'REPRESENTACIÓN ESTRATÉGICA.' : 'STRATEGIC REPRESENTATION.'}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-zinc-400 max-w-3xl mx-auto mb-8"
          >
            {language === 'es' 
              ? 'Agencia líder en representación de talento artístico, deportivo y digital en México y Latinoamérica. Transformamos carreras desde 2021.'
              : 'Leading talent representation agency for artists, athletes, and digital creators in Mexico and Latin America. Transforming careers since 2021.'}
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex justify-center gap-12 mb-12"
          >
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-white">
                <AnimatedCounter end={COMPANY_INFO.stats.talents} suffix="+" />
              </div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">{language === 'es' ? 'Talentos' : 'Talents'}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-white">{COMPANY_INFO.stats.reach}</div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">{language === 'es' ? 'Alcance' : 'Reach'}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-white">
                <AnimatedCounter end={COMPANY_INFO.stats.countries} suffix="+" />
              </div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">{language === 'es' ? 'Países' : 'Countries'}</div>
            </div>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to={language === 'es' ? '/contacto' : '/contact'}
              className="group flex items-center gap-3 bg-[#D91616] hover:bg-[#ED1C24] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all hover:-translate-y-1"
            >
              {language === 'es' ? 'Aplica para Representación' : 'Apply for Representation'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to={language === 'es' ? '/talento' : '/talent'}
              className="group flex items-center gap-3 border border-white/30 hover:border-[#D91616] text-white hover:text-[#D91616] px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all"
            >
              <Play className="w-4 h-4" />
              {language === 'es' ? 'Ver Talento' : 'View Talent'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
                {language === 'es' ? 'Quiénes Somos' : 'About Us'}
              </span>
              <h2 className="font-outfit text-4xl sm:text-5xl font-black tracking-tight uppercase text-white mb-6">
                {language === 'es' ? 'LA AGENCIA QUE TRANSFORMA CARRERAS' : 'THE AGENCY THAT TRANSFORMS CAREERS'}
              </h2>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                {language === 'es'
                  ? `Desde ${COMPANY_INFO.founded}, MERAKI Entertainment se ha consolidado como la agencia de representación más influyente de México. Combinamos pasión, estrategia y una red global de contactos para llevar a nuestros talentos al siguiente nivel.`
                  : `Since ${COMPANY_INFO.founded}, MERAKI Entertainment has established itself as Mexico's most influential representation agency. We combine passion, strategy, and a global network to take our talents to the next level.`}
              </p>
              <p className="text-zinc-500 mb-8">
                {language === 'es'
                  ? 'Nos encargamos de todo: desde la negociación de contratos hasta la gestión financiera completa, permitiendo que nuestros representados se enfoquen en lo que mejor saben hacer.'
                  : 'We handle everything: from contract negotiation to complete financial management, allowing our talents to focus on what they do best.'}
              </p>
              <Link
                to={language === 'es' ? '/nosotros' : '/about'}
                className="inline-flex items-center gap-2 text-[#D91616] font-semibold hover:gap-4 transition-all"
              >
                {language === 'es' ? 'Conoce nuestra historia' : 'Learn our story'}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/7433853/pexels-photo-7433853.jpeg?auto=compress&w=800"
                alt="MERAKI Team"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#D91616] p-6 text-white">
                <div className="text-4xl font-black">{COMPANY_INFO.stats.contracts}</div>
                <div className="text-sm uppercase tracking-wider">{language === 'es' ? 'Contratos Gestionados' : 'Contracts Managed'}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Servicios' : 'Services'}
            </span>
            <h2 className="font-outfit text-4xl sm:text-5xl font-black tracking-tight uppercase text-white mb-4">
              {language === 'es' ? 'SOLUCIONES INTEGRALES' : 'COMPREHENSIVE SOLUTIONS'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {Object.entries(SERVICES).map(([key, service], index) => {
              const Icon = serviceIcons[service.icon] || Mic2;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#0A0A0A] border border-white/10 p-6 group hover:-translate-y-2 transition-all"
                >
                  <div className="w-12 h-12 bg-[#D91616]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#D91616]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{service.title[language]}</h3>
                  <p className="text-sm text-zinc-500 mb-4">{service.description[language]}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center">
            <Link
              to={language === 'es' ? '/servicios' : '/services'}
              className="inline-flex items-center gap-2 text-[#D91616] font-semibold hover:gap-4 transition-all"
            >
              {language === 'es' ? 'Ver todos los servicios' : 'View all services'}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED TALENT */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Talento Destacado' : 'Featured Talent'}
            </span>
            <h2 className="font-outfit text-4xl sm:text-5xl font-black tracking-tight uppercase text-white">
              {language === 'es' ? 'NUESTROS REPRESENTADOS' : 'OUR ROSTER'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {featuredTalent.map((talent, index) => (
              <motion.div
                key={talent.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden bg-[#050505]"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={talent.image_url}
                    alt={talent.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="text-xs text-[#D91616] uppercase tracking-wider">{talent.specialty}</span>
                  <h3 className="text-lg font-bold text-white">{talent.name}</h3>
                  <p className="text-xs text-zinc-500">{talent.social.followers} followers</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to={language === 'es' ? '/talento' : '/talent'}
              className="inline-flex items-center gap-2 bg-[#D91616] hover:bg-[#ED1C24] text-white px-8 py-3 font-semibold uppercase tracking-wider transition-all"
            >
              {language === 'es' ? 'Ver todo el roster' : 'View full roster'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-[#D91616] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 border border-white rotate-45 -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 border border-white rotate-45 translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase text-white mb-6"
          >
            {language === 'es' ? '¿LISTO PARA EL SIGUIENTE NIVEL?' : 'READY FOR THE NEXT LEVEL?'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg mb-8 max-w-2xl mx-auto"
          >
            {language === 'es'
              ? 'Únete a MERAKI Entertainment y transforma tu carrera con el respaldo de la agencia más influyente de México.'
              : 'Join MERAKI Entertainment and transform your career with the backing of Mexico\'s most influential agency.'}
          </motion.p>
          <Link
            to={language === 'es' ? '/contacto' : '/contact'}
            className="inline-flex items-center gap-3 bg-white text-[#D91616] px-8 py-4 font-bold uppercase tracking-wider hover:bg-zinc-100 transition-all"
          >
            {language === 'es' ? 'Contáctanos Hoy' : 'Contact Us Today'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* CONTACT INFO PREVIEW */}
      <section className="py-16 bg-[#050505] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <h4 className="text-white font-semibold mb-2">{language === 'es' ? 'Oficina Central' : 'Head Office'}</h4>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.address.street}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.address.floor}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.address.colony}, {COMPANY_INFO.address.city}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">{language === 'es' ? 'Contacto' : 'Contact'}</h4>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.contact.phone}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.contact.email}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">{language === 'es' ? 'Horario' : 'Hours'}</h4>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.hours.weekdays}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.hours.saturday}</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
